import { PI, SECONDS_PER_DAY } from "../math/constants";
import { stateToElements } from "../math/orbital_math";
import type { OrbitalElements } from "../math/orbital_elements";
import { periapsis, apoapsis } from "../math/orbital_elements";
import { Vector3d } from "../math/vector3d";
import { BodyRegistry } from "../bodies/body_registry";
import { BodyType } from "../bodies/celestial_body_data";
import { SpacecraftState } from "../spacecraft/spacecraft_state";
import type { ConditionEvaluator } from "../crux/condition_evaluator";
import { TimeManager } from "./time_manager";
import { ManeuverQueue, type Maneuver, executeImpulsive } from "./maneuver";
import { propagate, trueAnomalyAt } from "./orbit_propagator";
import { SOIResolver } from "./soi_resolver";
import { SimulationState } from "./simulation_state";
import { SimulationEvents } from "./simulation_events";

/**
 * The main simulation tick loop. Owns all simulation state and drives updates.
 * Pure TypeScript -- no framework dependency.
 */
export class SimulationEngine {
  readonly time: TimeManager;
  readonly state: SimulationState;
  readonly maneuvers: ManeuverQueue;
  readonly events: SimulationEvents;

  private readonly bodyRegistry: BodyRegistry;
  private readonly soiResolver: SOIResolver;
  private conditionEvaluator: ConditionEvaluator | null = null;
  private cruxFinished: boolean = false;

  constructor(
    bodyRegistry: BodyRegistry,
    spacecraft: SpacecraftState,
    initialSOIBody: string,
    startTimeJD: number,
  ) {
    this.bodyRegistry = bodyRegistry;
    this.soiResolver = new SOIResolver(bodyRegistry);

    this.time = new TimeManager(startTimeJD);
    this.maneuvers = new ManeuverQueue();
    this.events = new SimulationEvents();

    // Compute initial orbital elements
    const gm = bodyRegistry.getGM(initialSOIBody);
    const orbit = stateToElements(spacecraft.state, gm);

    this.state = new SimulationState({
      spacecraft,
      currentSOIBody: initialSOIBody,
      currentOrbit: orbit,
      startTimeJD,
    });
  }

  setConditionEvaluator(evaluator: ConditionEvaluator): void {
    this.conditionEvaluator = evaluator;
  }

  /**
   * Main tick. Call once per frame with real-world delta time.
   * @param realDeltaSeconds Real-world seconds since last tick
   */
  tick(realDeltaSeconds: number): void {
    // 1. If paused, skip
    if (this.time.isPaused || this.cruxFinished) return;

    // 2. Advance time
    const simDeltaDays = this.time.advance(realDeltaSeconds);
    if (simDeltaDays <= 0) return;

    const currentTimeJD = this.time.currentTimeJD;
    let gm = this.bodyRegistry.getGM(this.state.currentSOIBody);

    // 3. Check maneuver queue for burns in this timestep
    const maneuver = this.maneuvers.popNextDue(currentTimeJD);
    if (maneuver) {
      this.executeManeuver(maneuver, gm);
    }

    // 4. Sub-stepped propagation + SOI check
    // At high time warp, a single step can skip an entire SOI.
    // Sub-step so each step covers < half the smallest relevant SOI radius.
    const startTimeJD = currentTimeJD - simDeltaDays;
    const subSteps = this.computeSubStepCount(simDeltaDays);

    if (subSteps <= 1) {
      // Fast path: single step (low time warp or large SOIs)
      const newState = propagate(this.state.currentOrbit, gm, currentTimeJD);
      this.state.spacecraft.state = newState;
      this.checkSOITransition(currentTimeJD);
    } else {
      // Sub-step: propagate in smaller increments, checking SOI at each
      const subDelta = simDeltaDays / subSteps;
      // Cache body positions once (they barely change within one frame)
      const bodyPositions = this.bodyRegistry.computeAllPositions(currentTimeJD);

      for (let i = 1; i <= subSteps; i++) {
        const subTimeJD = startTimeJD + subDelta * i;
        gm = this.bodyRegistry.getGM(this.state.currentSOIBody);
        const subState = propagate(this.state.currentOrbit, gm, subTimeJD);
        this.state.spacecraft.state = subState;

        if (this.checkSOITransitionWithPositions(subTimeJD, bodyPositions)) {
          // SOI changed — orbit recomputed in new frame.
          // Continue sub-stepping in new SOI (remaining steps will use new orbit).
          gm = this.bodyRegistry.getGM(this.state.currentSOIBody);
        }
      }
    }

    // 5. Check periapsis/apoapsis passage
    gm = this.bodyRegistry.getGM(this.state.currentSOIBody);
    this.checkApsides(gm, currentTimeJD);

    // 6. Evaluate conditions
    if (this.conditionEvaluator) {
      this.evaluateConditions();
    }

    // 7. Record trajectory history
    this.state.recordTrajectoryPoint(this.state.spacecraft.state.position);

    // 8. Fire events
    this.events.fireSpacecraftMoved(this.state.spacecraft.state);
    this.events.fireTimeChanged(currentTimeJD);
  }

  /**
   * Compute how many sub-steps are needed so each step covers less than
   * half the smallest relevant SOI radius.
   */
  private computeSubStepCount(simDeltaDays: number): number {
    const speed = this.state.spacecraft.state.velocity.magnitude; // km/s
    const simDeltaSeconds = simDeltaDays * SECONDS_PER_DAY;
    const distanceKm = speed * simDeltaSeconds;

    const minSOI = this.getSmallestRelevantSOI();
    if (minSOI <= 0 || !isFinite(minSOI)) return 1;

    const maxStepDistance = minSOI * 0.5;
    const steps = Math.ceil(distanceKm / maxStepDistance);
    return Math.min(Math.max(steps, 1), 64);
  }

  /**
   * Get the smallest SOI radius that matters for the current situation.
   * When in Sun SOI: smallest planet SOI we might encounter.
   * When in a planet SOI: smallest moon SOI, or the planet's own SOI (for exit detection).
   */
  private getSmallestRelevantSOI(): number {
    const currentBody = this.bodyRegistry.getByName(this.state.currentSOIBody);
    if (!currentBody) return Infinity;

    if (this.state.currentSOIBody === "Sun") {
      // Find smallest planet SOI
      let minSOI = Infinity;
      for (const body of this.bodyRegistry.allBodies) {
        if (body.parentBodyName === "Sun" && body.soiRadius > 0 && body.soiRadius < minSOI) {
          minSOI = body.soiRadius;
        }
      }
      return minSOI;
    } else {
      // In a planet's SOI: check children (moons) and own SOI boundary
      let minSOI = currentBody.soiRadius; // for exit detection
      for (const child of this.bodyRegistry.getChildren(currentBody)) {
        if (child.soiRadius > 0 && child.soiRadius < minSOI) {
          minSOI = child.soiRadius;
        }
      }
      return minSOI;
    }
  }

  private executeManeuver(maneuver: Maneuver, gm: number): void {
    const fuelBefore = this.state.spacecraft.fuelMass;
    const actualDv = executeImpulsive(this.state.spacecraft, maneuver);
    const fuelUsed = fuelBefore - this.state.spacecraft.fuelMass;

    this.state.totalDeltaVUsed += actualDv;

    // Recompute orbital elements after maneuver
    this.state.currentOrbit = stateToElements(this.state.spacecraft.state, gm);

    this.events.fireManeuverExecuted(actualDv, fuelUsed);
    this.events.fireOrbitChanged(this.state.currentOrbit);
    this.events.fireFuelFractionChanged(this.state.spacecraft.fuelFraction);
    this.events.fireDeltaVChanged(this.state.spacecraft.availableDeltaV);
  }

  private checkSOITransition(jd: number): boolean {
    return this.checkSOITransitionWithPositions(jd, null);
  }

  /**
   * Check and handle SOI transition. Uses cached body positions if provided.
   * Returns true if a transition occurred.
   */
  private checkSOITransitionWithPositions(jd: number, cachedPositions: Map<string, Vector3d> | null): boolean {
    // Get heliocentric position of spacecraft
    let helioPos: Vector3d;
    if (this.state.currentSOIBody === "Sun") {
      helioPos = this.state.spacecraft.state.position;
    } else {
      if (cachedPositions) {
        // Use cached body position for efficiency
        const bodyHelioPos = cachedPositions.get(this.state.currentSOIBody) || Vector3d.Zero;
        helioPos = bodyHelioPos.add(this.state.spacecraft.state.position);
      } else {
        const bodyState = this.bodyRegistry.computeBodyState(this.state.currentSOIBody, jd);
        const body = this.bodyRegistry.getByName(this.state.currentSOIBody);
        if (body && body.bodyType === BodyType.Moon) {
          const parentState = this.bodyRegistry.computeBodyState(body.parentBodyName!, jd);
          helioPos = parentState.position.add(bodyState.position).add(this.state.spacecraft.state.position);
        } else {
          helioPos = bodyState.position.add(this.state.spacecraft.state.position);
        }
      }
    }

    const result = this.soiResolver.checkTransition(helioPos, this.state.currentSOIBody, jd);
    if (result.transitioned) {
      const oldSOI = this.state.currentSOIBody;

      // Transform state to new reference frame
      this.state.spacecraft.state = this.soiResolver.transformToNewSOI(
        this.state.spacecraft.state,
        oldSOI,
        result.newSOIBody,
        jd,
      );

      this.state.currentSOIBody = result.newSOIBody;

      // Recompute orbit in new frame
      const newGM = this.bodyRegistry.getGM(result.newSOIBody);
      this.state.currentOrbit = stateToElements(this.state.spacecraft.state, newGM);

      this.events.fireSOIChanged(result.newSOIBody, true);
      this.events.fireOrbitChanged(this.state.currentOrbit);
      return true;
    }
    return false;
  }

  private checkApsides(gm: number, currentTimeJD: number): void {
    const currentNu = trueAnomalyAt(this.state.currentOrbit, gm, currentTimeJD);
    const prevNu = this.state.previousTrueAnomaly;

    // Periapsis at nu = 0 (crossing from 2pi to 0)
    if (prevNu > PI && currentNu < PI && currentNu < 0.1) {
      this.events.firePeriapsisPassage(periapsis(this.state.currentOrbit));
    }

    // Apoapsis at nu = pi
    if (prevNu < PI && currentNu >= PI) {
      this.events.fireApoapsisPassage(apoapsis(this.state.currentOrbit));
    }

    this.state.previousTrueAnomaly = currentNu;
  }

  private evaluateConditions(): void {
    if (!this.conditionEvaluator) return;

    const results = this.conditionEvaluator.evaluate(
      this.state,
      this.bodyRegistry,
      this.time.currentTimeJD,
    );

    for (const { conditionId, isMet, isVictory } of results) {
      if (!isMet) continue;
      if (isVictory) {
        this.events.fireVictoryConditionMet(conditionId);
      } else {
        this.events.fireFailureConditionMet(conditionId);
      }
    }
  }

  /** Mark the crux as finished (stops simulation). */
  finishCrux(): void {
    this.cruxFinished = true;
    this.time.isPaused = true;
  }

  // Public control methods

  setTimeAcceleration(factor: number): void {
    this.time.timeAcceleration = factor;
  }

  togglePause(): void {
    this.time.togglePause();
    this.events.firePauseChanged(this.time.isPaused);
  }

  pause(): void {
    this.time.isPaused = true;
    this.events.firePauseChanged(true);
  }

  addManeuver(maneuver: Maneuver): string {
    return this.maneuvers.add(maneuver);
  }

  removeManeuver(id: string): boolean {
    return this.maneuvers.remove(id);
  }

  /** Force an SOI check at the current state. Used after synchronous burns. */
  forceSOICheck(): boolean {
    return this.checkSOITransition(this.time.currentTimeJD);
  }
}
